import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  IAuthWithSmsRequest,
  IAuthWithSmsResponse,
  ICancelOrderResponse,
  IChangeClientPhoneWithCodeRequest,
  ICreateOrderRequest,
  ICreateOrderResponse,
  IDriverPosition,
  IGetClientCardsResponse,
  IGetClientOrderHistoryRequest,
  IGetClientOrderHistoryResponse,
  IGetClientProfileResponse,
  IGetConfigResponse,
  IGetDriversPositionRequest,
  IGetDriversPositionResponse,
  IGetGeoDataRequest,
  IGetGeoDataResponse,
  IGetNearestGeoDataRequest,
  IGetOrderDetailsResponse,
  IGetSettingsResponse,
  IGetStreetsRequest,
  IGetStreetsResponse,
  IGetTariffsRequest,
  IGetTariffsResponse,
  IUpdateClientProfileRequest,
} from "./types";
import { RootState } from "store";
import { generateAuthHeader, getToken, LOCAL_STORAGE_KEY } from "./constants";

export const webordersApi = createApi({
  reducerPath: "webordersApi",
  baseQuery: async (args, api, extraOptions) => {
    const { getState, dispatch } = api;

    const state = getState() as RootState;
    const apiVersion = state.settings.apiVersion;

    if (!apiVersion) {
      try {
        await dispatch(
          webordersApi.endpoints.getApiVersion.initiate(),
        ).unwrap();
      } catch (error) {
        console.error("Failed to fetch API version", error);
        throw error;
      }
    }

    return fetchBaseQuery({
      baseUrl: process.env.REACT_APP_BASE_URL,
      prepareHeaders: (headers) => {
        const updatedState = api.getState() as RootState;
        const updatedApiVersion = updatedState.settings.apiVersion;
        headers.set("X-API-VERSION", updatedApiVersion);
        const token = getToken(LOCAL_STORAGE_KEY) || state.auth.token;
        headers.set("Content-Type", "application/json");
        headers.set("X-WO-API-APP-ID", "weborders-widget");
        headers.set("Accept-Language", "UK-UA");
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        } else {
          const defaultLogin = process.env.REACT_APP_DEFAULT_USER_LOGIN;
          const defaultPassword = process.env.REACT_APP_DEFAULT_USER_PASSWORD;
          if (defaultLogin && defaultPassword) {
            headers.set(
              "Authorization",
              generateAuthHeader(defaultLogin, defaultPassword),
            );
          } else {
            console.error("DEFAULT_USER credentials are not defined");
          }
        }

        return headers;
      },
    })(args, api, extraOptions);
  },
  tagTypes: ["Auth", "UpdateProfile", "CancelOrder"],
  endpoints: (builder) => ({
    getApiVersion: builder.query<{ version: string }, void>({
      query: () => ({
        url: "/api/version",
      }),
    }),
    getSettings: builder.query<IGetSettingsResponse, void>({
      query: () => ({
        url: "api/settings",
      }),
    }),
    getConfig: builder.query<IGetConfigResponse, void>({
      query: () => ({
        url: "/conf",
      }),
    }),
    getGeoData: builder.query<IGetGeoDataResponse, IGetGeoDataRequest>({
      query: (params) => ({
        url: "api/geodata/search",
        params: {
          ...params,
          offset: 0,
          limit: 300,
          transliteration: true,
          qwertySwitcher: true,
        },
      }),
    }),
    getStreets: builder.query<IGetStreetsResponse, IGetStreetsRequest>({
      query: (params) => ({
        url: "api/geodata/streets/search",
        params: {
          ...params,
          offset: 0,
          limit: 300,
          transliteration: true,
          qwertySwitcher: true,
        },
      }),
    }),
    getNearestGeoData: builder.query<
      IGetGeoDataResponse,
      IGetNearestGeoDataRequest
    >({
      query: (params) => ({
        url: "api/geodata/nearest",
        params: params,
      }),
    }),
    getTariffs: builder.mutation<IGetTariffsResponse[], IGetTariffsRequest>({
      query: (requestParams) => ({
        url: "/api/weborders/tariffs/cost",
        method: "POST",
        body: JSON.stringify(requestParams),
      }),
    }),
    getSmsAuthCode: builder.mutation<void, string>({
      query: (phone) => ({
        url: "/api/v2.0/account/getSMSAuthCode",
        method: "POST",
        body: JSON.stringify({ phone }),
      }),
    }),
    authWithSms: builder.mutation<IAuthWithSmsResponse, IAuthWithSmsRequest>({
      query: (payload) => ({
        url: "/api/v2.0/account/authWithSMS",
        method: "POST",
        body: JSON.stringify({
          phone: payload.phone,
          ["confirm_code"]: payload.confirm_code,
        }),
        invalidatesTags: ["Auth"],
      }),
    }),
    getClientProfile: builder.query<IGetClientProfileResponse, void>({
      query: () => ({
        url: "api/clients/profile",
      }),
      providesTags: ["Auth", "UpdateProfile"],
    }),
    createOrder: builder.mutation<ICreateOrderResponse, ICreateOrderRequest>({
      query: (requestParams) => ({
        url: "/api/weborders",
        method: "POST",
        body: JSON.stringify(requestParams),
      }),
    }),
    getOrderDetails: builder.query<IGetOrderDetailsResponse, string>({
      query: (id) => ({
        url: `/api/v2.0/weborders/${id}`,
      }),
    }),
    getAddCost: builder.query<{ Amount: number }, string>({
      query: (id) => ({
        url: `/api/weborders/${id}/cost/additional`,
      }),
    }),
    addToAddCost: builder.mutation<
      IGetOrderDetailsResponse,
      { id: string; amount: number }
    >({
      query: ({ id, amount }) => ({
        url: `/api/weborders/${id}/cost/additional`,
        method: "POST",
        body: JSON.stringify({ amount }),
      }),
    }),
    updateAddCost: builder.mutation<
      IGetOrderDetailsResponse,
      { id: string; amount: number }
    >({
      query: ({ id, amount }) => ({
        url: `/api/weborders/${id}/cost/additional`,
        method: "PUT",
        body: JSON.stringify({ amount }),
      }),
    }),
    cancelOrder: builder.mutation<ICancelOrderResponse, string>({
      query: (id) => ({
        url: `/api/v2.0/weborders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CancelOrder"],
    }),
    getClientOrdersHistory: builder.query<
      IGetClientOrderHistoryResponse[],
      IGetClientOrderHistoryRequest
    >({
      query: (params) => ({
        url: "api/v2.0/weborders",
        params: params,
      }),
      providesTags: ["CancelOrder"],
    }),
    changeClientPhone: builder.mutation<void, string>({
      query: (phone) => ({
        url: "/api/clients/changePhone/sendConfirmCode",
        method: "POST",
        body: JSON.stringify({ phone }),
      }),
    }),
    changeClientPhoneWithCode: builder.mutation<
      IAuthWithSmsResponse,
      IChangeClientPhoneWithCodeRequest
    >({
      query: (payload) => ({
        url: "/api/v2.0/clients/changePhone/",
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    }),
    updateClientProfile: builder.mutation<void, IUpdateClientProfileRequest>({
      query: (payload) => ({
        url: "/api/clients/profile",
        method: "PUT",
        body: JSON.stringify(payload),
      }),
      invalidatesTags: ["UpdateProfile"],
    }),
    setTokenForPush: builder.mutation<void, string>({
      query: (payload) => ({
        url: "/api/clients/credential",
        method: "PUT",
        body: JSON.stringify({ ["app_registration_token"]: payload }),
      }),
    }),
    getDriversPosition: builder.query<
      IGetDriversPositionResponse[],
      IGetDriversPositionRequest
    >({
      query: (params) => ({
        url: "/api/drivers/position",
        params: params,
      }),
    }),
    getDriverCarPosition: builder.query<IDriverPosition, string>({
      query: (id) => ({
        url: `/api/weborders/drivercarposition/${id}`,
      }),
    }),
    getWayForPayData: builder.query<string, void>({
      query: () => ({
        url: "/api/clients/cards/fondy/binddata",
        responseHandler: "text",
      }),
    }),
    getClientCards: builder.query<IGetClientCardsResponse, void>({
      query: () => ({
        url: "/api/clients/cards",
      }),
    }),
    removeClientCard: builder.mutation<void, string>({
      query: (cardId) => ({
        url: `/api/clients/cards/${cardId}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetApiVersionQuery,
  useGetSettingsQuery,
  useGetConfigQuery,
  useGetGeoDataQuery,
  useGetStreetsQuery,
  useGetNearestGeoDataQuery,
  useGetTariffsMutation,
  useGetSmsAuthCodeMutation,
  useAuthWithSmsMutation,
  useGetClientProfileQuery,
  useCreateOrderMutation,
  useGetOrderDetailsQuery,
  useGetAddCostQuery,
  useAddToAddCostMutation,
  useUpdateAddCostMutation,
  useCancelOrderMutation,
  useGetClientOrdersHistoryQuery,
  useChangeClientPhoneMutation,
  useChangeClientPhoneWithCodeMutation,
  useUpdateClientProfileMutation,
  useSetTokenForPushMutation,
  useGetDriversPositionQuery,
  useGetDriverCarPositionQuery,
  useGetWayForPayDataQuery,
  useGetClientCardsQuery,
  useRemoveClientCardMutation,
} = webordersApi;

export default webordersApi;
